import { useEffect, useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import logoSrc from "@/assets/logo.png";
import { ApiDocs } from "./docs/ApiDocs";
import { Actualite } from "./docs/Actualite";

type Page = "actualite" | "tutoriels" | "api" | "mcp";

const PAGES: Page[] = ["actualite", "tutoriels", "api", "mcp"];

/** /docs#api, /docs#api-ecrans… open the matching page (links from the app). */
function pageFromHash(): Page {
  const head = window.location.hash.replace(/^#/, "").split("-")[0];
  return (PAGES as string[]).includes(head) ? (head as Page) : "actualite";
}

function NavBar() {
  return (
    <header className="sticky top-0 z-40 border-b border-gray-100 bg-white/90 backdrop-blur">
      <div className="mx-auto flex h-14 max-w-none items-center justify-between px-6">
        <a href="/" className="flex items-center gap-2">
          <img src={logoSrc} alt="Flowboard" className="h-6 w-auto" />
          <span className="text-sm font-semibold text-gray-800">Flowboard</span>
        </a>
        <a href="/" className="text-sm text-gray-500 transition-colors hover:text-gray-800">
          ← Retour
        </a>
      </div>
    </header>
  );
}

function MarkdownPage({ slug }: { slug: string }) {
  const titles: Record<string, string> = {
    tutoriels: "Tutoriels",
    api: "API",
    mcp: "MCP",
  };
  return (
    <div className="max-w-2xl">
      <h1 className="mb-6 text-2xl font-bold text-gray-900">{titles[slug] ?? slug}</h1>
      <p className="text-gray-400 italic">Documentation en cours de rédaction.</p>
    </div>
  );
}

interface SidebarItemProps {
  label: string;
  active: boolean;
  onClick: () => void;
}

function SidebarLeaf({ label, active, onClick }: SidebarItemProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex w-full items-center rounded-md px-3 py-2 text-left text-sm transition-colors ${
        active
          ? "border-l-2 border-indigo-600 bg-indigo-50 pl-[10px] font-medium text-indigo-700"
          : "text-gray-700 hover:bg-gray-100"
      }`}
    >
      {label}
    </button>
  );
}

function SidebarGroup({
  label,
  open,
  onToggle,
  children,
}: {
  label: string;
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  return (
    <div>
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center gap-1.5 rounded-md px-3 py-2 text-left text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100"
      >
        {open ? (
          <ChevronDown className="h-4 w-4 shrink-0 text-gray-400" />
        ) : (
          <ChevronRight className="h-4 w-4 shrink-0 text-gray-400" />
        )}
        {label}
      </button>
      {open && <div className="ml-4 mt-0.5 space-y-0.5 border-l border-gray-200 pl-2">{children}</div>}
    </div>
  );
}

export function DocsPage() {
  const [currentPage, setCurrentPage] = useState<Page>(pageFromHash);
  const [docsOpen, setDocsOpen] = useState(true);

  useEffect(() => {
    const onHash = () => setCurrentPage(pageFromHash());
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  // Deep link to a section (#api-ecrans): scroll once the page is rendered.
  useEffect(() => {
    const id = window.location.hash.slice(1);
    if (id.includes("-")) document.getElementById(id)?.scrollIntoView();
  }, [currentPage]);

  const go = (page: Page) => {
    setCurrentPage(page);
    window.history.replaceState(null, "", page === "actualite" ? "/docs" : `/docs#${page}`);
    window.scrollTo({ top: 0 });
  };

  return (
    <div className="flex min-h-screen flex-col bg-white font-sans text-gray-900">
      <NavBar />
      <div className="flex flex-1">
        {/* Sidebar */}
        <aside className="hidden w-60 shrink-0 border-r border-gray-100 bg-gray-50 px-3 py-6 md:block">
          <p className="mb-3 px-3 text-xs font-semibold uppercase tracking-wider text-gray-400">
            Navigation
          </p>
          <nav className="space-y-0.5">
            <SidebarLeaf
              label="Actualité"
              active={currentPage === "actualite"}
              onClick={() => go("actualite")}
            />
            <SidebarGroup
              label="Docs"
              open={docsOpen}
              onToggle={() => setDocsOpen((o) => !o)}
            >
              <SidebarLeaf
                label="Tutoriels"
                active={currentPage === "tutoriels"}
                onClick={() => go("tutoriels")}
              />
              <SidebarLeaf
                label="API"
                active={currentPage === "api"}
                onClick={() => go("api")}
              />
              <SidebarLeaf
                label="MCP"
                active={currentPage === "mcp"}
                onClick={() => go("mcp")}
              />
            </SidebarGroup>
          </nav>
        </aside>

        {/* Main content */}
        <main className="min-w-0 flex-1 overflow-y-auto px-4 py-6 md:px-10 md:py-8">
          {/* Phones: the sidebar becomes a row of tabs. */}
          <div className="-mx-1 mb-6 flex gap-1 overflow-x-auto md:hidden">
            {(
              [
                ["actualite", "Actualité"],
                ["tutoriels", "Tutoriels"],
                ["api", "API"],
                ["mcp", "MCP"],
              ] as const
            ).map(([page, label]) => (
              <button
                key={page}
                type="button"
                onClick={() => go(page)}
                className={`shrink-0 rounded-lg px-3 py-1.5 text-sm ${
                  currentPage === page ? "bg-indigo-50 font-medium text-indigo-700" : "text-gray-600 hover:bg-gray-100"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          {currentPage === "actualite" && <Actualite />}
          {currentPage === "api" && <ApiDocs />}
          {(currentPage === "tutoriels" || currentPage === "mcp") && <MarkdownPage slug={currentPage} />}
        </main>
      </div>
    </div>
  );
}
